import { useMe } from '@crehana/auth';
import { PageError } from '@crehana/compass.ui/page-error';
import { PageLoader } from '@crehana/compass.ui/v2';
import { FeatureFlagProvider } from '@crehana/feature-flags/react';
import type { TAppProps } from '@crehana/next';
import { useRouter } from 'next/router';

import { useOrganizationChildrenQuery } from '@/shared/hooks/use-organization-children-query';

import { COURSE_DETAIL_AMPLITUDE_PAGE_NAME } from '../constants';
import type { TCourseDetailProps } from '../types';

export function withProviders(
  Component: React.FC<TAppProps<TCourseDetailProps>>,
) {
  const Providers = (props: TAppProps<TCourseDetailProps>) => {
    const { loading: loadingUser, me } = useMe();
    const { query } = useRouter();
    const { orgSlug } = query as { orgSlug: string };
    const organizationSlug = me?.organization?.slug || orgSlug;

    /**
     * Get organization children to pass the organizationId to the course detail
     */
    const { loading: loadingOrganizationChildren, organizationChildren } =
      useOrganizationChildrenQuery({
        slug: organizationSlug!,
        skip: !organizationSlug,
      });

    const organizationHasChildren = organizationChildren?.find(
      org => org?.value === props.orgSlug,
    );

    const organizationId =
      organizationHasChildren?.originalId || me?.organization?.originalId;

    const isLoading = loadingUser || loadingOrganizationChildren;

    if (isLoading) {
      return <PageLoader />;
    }

    if (typeof organizationId !== 'number') {
      return (
        <PageError
          amplitudePageName={COURSE_DETAIL_AMPLITUDE_PAGE_NAME}
          organizationSlug={organizationSlug}
        />
      );
    }

    return (
      <FeatureFlagProvider organizationId={organizationId}>
        <Component
          {...props}
          organizationId={organizationId}
          organizationSlug={organizationSlug}
        />
      </FeatureFlagProvider>
    );
  };

  Providers.displayName = `CourseDetailProvider`;

  return Providers;
}
